/**
 * Mark someone away or back (desk-and-away-contract.md §6), from the user menu, Settings → Teams
 * and Settings → Users. One place for the call, the toasts and the refresh, so every place says
 * the same thing.
 *
 * After a change the app's cached user records are refreshed:
 * - the signed-in person's own record (the auth store), when the change was to them — at once
 *   from the answer, then re-read from `/auth/me` (which also tells their other open tabs);
 * - the shared users list (`useUsersSummaryStore`), re-read in the background.
 * The screen that asked re-reads its own list (`onChanged`).
 */

import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { authFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useUsersSummaryStore } from '@/stores/users-summary-store'
import { changeAway, requestSetAway, type AwayFields, type AwayNotifier, type AwayOutcome } from './away'

const sonnerNotifier: AwayNotifier = {
  success: (title) => toast.success(title),
  error: (title, options) => toast.error(title, options),
}

/** Put the signed-in person's new away fields into the auth store's record of them. */
function applyToMe(fields: AwayFields): void {
  useAuthStore.setState((state) =>
    state.user
      ? { user: { ...state.user, away: fields.away ?? undefined, awaySince: fields.awaySince ?? null, awaySetBy: fields.awaySetBy ?? null } }
      : {},
  )
}

function refreshCaches(self: boolean): void {
  const auth = useAuthStore.getState()
  if (self) void auth.refreshPermissions()
  const token = auth.tokens?.accessToken
  if (token) {
    void useUsersSummaryStore
      .getState()
      .fetch(token, false, true)
      .catch(() => {
        // The store keeps its own error; a stale list is only a stale chip.
      })
  }
}

/**
 * @param onChanged Re-reads the asking screen's own list after a change or a refusal.
 * @returns `setAway`, and `savingId` — the person whose change is on its way, for their switch.
 */
export function useSetAway(onChanged?: () => void): {
  setAway: (target: { userId: string; name?: string | null }, next: boolean) => Promise<AwayOutcome>
  savingId: string | null
} {
  const [savingId, setSavingId] = useState<string | null>(null)

  const setAway = useCallback(
    async (target: { userId: string; name?: string | null }, next: boolean): Promise<AwayOutcome> => {
      const { tokens, user } = useAuthStore.getState()
      const token = tokens?.accessToken
      if (!token) return 'failed'
      setSavingId(target.userId)
      try {
        const outcome = await changeAway(
          {
            request: (userId, away) => requestSetAway(authFetch, token, userId, away),
            myUserId: user?.userId,
            applyToMe,
            refreshCaches,
            notify: sonnerNotifier,
          },
          target,
          next,
        )
        if (outcome !== 'failed') onChanged?.()
        return outcome
      } finally {
        setSavingId(null)
      }
    },
    [onChanged],
  )

  return { setAway, savingId }
}
